import { Badge, Card, Checkbox, Group, Stack, Text, useMantineTheme } from '@mantine/core';

import { format } from 'date-fns';

import { useParams } from 'react-router-dom';
import { IconCalendarWeek, IconFile, IconFolderPin } from '@tabler/icons-react';

import { type OrganizationResourceTag, type OrganizationResource } from '../../types/apiTypes';
import { ThreeDotsMenu } from '../DropdownMenus';
import { ResourceTableTagsContainer } from '../ResourceTableTagsContainer';
import { RESOURCE_TYPE_TO_STRING_MAP } from '../../utils/constants';
import { type ResourceType } from '../../types/miscTypes';
import { hasPermissionsForObject } from '../../utils/apiUtils';

interface Props {
  resource: OrganizationResource
  organizationResourceTags: OrganizationResourceTag[]
  onOpenEdit: (resource: OrganizationResource) => void
  onOpenDelete: (resource: OrganizationResource) => void
  onDownload: (resource: OrganizationResource) => void
  onEditResourceTags: () => void
  selected?: boolean
  onSelect?: (resourceUid: string) => void
}

const ResourceListItem: React.FC<Props> = (props: Props) => {
  const {
    resource,
    organizationResourceTags,
    onDownload,
    onOpenDelete,
    onOpenEdit,
    onEditResourceTags,
    onSelect,
    selected = false
  } = props;
  const { organization } = useParams();

  const { other: colorVars, colors } = useMantineTheme();
  const dateObj = new Date(resource.created_at ?? '');
  const formattedDate = format(dateObj, 'M/d/yy');

  const menuItems = [
    {
      label: 'Edit',
      onClick: () => { onOpenEdit(resource); },
      shouldRender: hasPermissionsForObject(resource, 'change')
    },
    {
      label: 'Delete',
      onClick: () => { onOpenDelete(resource); },
      color: colors.red[5],
      shouldRender: hasPermissionsForObject(resource, 'delete')
    },
    {
      label: 'Download',
      onClick: () => { onDownload(resource); },
      shouldRender: true
    }
  ];

  return (
    <Card withBorder mb={8} shadow='2px 2px 4px 0px #0000001A'>
      <Stack gap={12} ta='start'>
        <Group justify='space-between'>
          <Group gap={8}>
            {onSelect !== undefined && <Checkbox
              checked={selected}
              onChange={() => { onSelect(resource.uid); }}
              size='xs'
              mr={4}
            />}
            {
              resource.processing_status === 'PROCESSING' && (
                <Badge color={colorVars.processingColor} style={{ borderRadius: 4 }}>
                  Processing
                </Badge>)
            }
            <Text fw={500}>
              {resource.name}
            </Text>
          </Group>
          <ThreeDotsMenu menuItems={menuItems} />
        </Group>
        <Group gap={16}>
          <Group gap={8}>
            <IconCalendarWeek color={colorVars.secondaryIconColor} size={20} />
            <Text fz='sm' fw={500} c={colorVars.secondaryTextColor}> {formattedDate} </Text>
          </Group>
          <Group gap={8}>
            <IconFile color={colorVars.secondaryIconColor} size={20} />
            <Text fz='sm' fw={500} c={colorVars.secondaryTextColor}>
              {RESOURCE_TYPE_TO_STRING_MAP[resource.resource_type as ResourceType]}
            </Text>
          </Group>
          <Group gap={8}>
            <IconFolderPin color={colorVars.secondaryIconColor} size={20} />
            <Text fz='sm' fw={500} c={colorVars.secondaryTextColor}>
              {resource.storage_type === 'SHAREPOINT' ? 'Sharepoint' : 'Ensis'}
            </Text>
          </Group>
        </Group>
        <Text fz='sm'>
          {resource.description}
        </Text>
        <ResourceTableTagsContainer
          onEditResourceTags={onEditResourceTags}
          resource={resource}
          organizationSlug={organization ?? ''}
          tags={organizationResourceTags}
        />
      </Stack>
    </Card >
  );
};

export default ResourceListItem;
